/**
 * Compartir la invitación con quien va a usarla.
 *
 * Lo que se comparte es **la imagen del QR**, no el token. El kiosco escanea un
 * código; no tiene dónde teclear una cadena de 200 caracteres. Mandar el texto
 * dejaba al visitante con algo que no podía usar en la reja, y ese era el
 * problema real detrás de «solo puedo copiar».
 *
 * Tres caminos, y hacen falta los tres:
 *
 * 1. **En el APK**, el plugin de Capacitor. El WebView de Android **no**
 *    implementa `navigator.share` —es una API de Chrome, no del WebView—, así
 *    que ahí el botón caía siempre al portapapeles sin que nada lo delatara.
 * 2. **En un navegador de móvil**, `navigator.share` con el archivo, que abre
 *    la hoja de compartir del sistema.
 * 3. **En escritorio**, descargar la imagen. No hay hoja de compartir, y copiar
 *    un token al portapapeles no le sirve a nadie.
 */

import { esAppNativa } from './servidor';

export type ResultadoCompartir = 'compartido' | 'descargado' | 'cancelado';

/** Convierte el `data:` que produce la librería de QR en bytes. */
function bytesDeDataUri(dataUri: string): Blob {
  const [cabecera, datos] = dataUri.split(',');
  const tipo = /:(.*?);/.exec(cabecera ?? '')?.[1] ?? 'image/png';
  const binario = atob(datos ?? '');
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return new Blob([bytes], { type: tipo });
}

/** `true` si el error es el usuario cerrando la hoja de compartir. */
function loCancelaron(e: unknown): boolean {
  const mensaje = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /abort|cancel/i.test(mensaje);
}

/**
 * Comparte el QR de una invitación.
 *
 * @param imagenQr  El `data:image/png;base64,…` ya generado en pantalla.
 * @param titulo    Encabezado del diálogo del sistema.
 * @param texto     Lo que acompaña a la imagen en el mensaje.
 * @param archivo   Nombre del archivo; lo ve quien lo recibe.
 */
export async function compartirImagen(
  imagenQr: string,
  { titulo, texto, archivo }: { titulo: string; texto: string; archivo: string },
): Promise<ResultadoCompartir> {
  if (esAppNativa()) {
    const { Share } = await import('@capacitor/share');
    const { Filesystem, Directory } = await import('@capacitor/filesystem');

    // El plugin comparte archivos del disco, no datos en memoria, así que el
    // PNG se escribe primero. Va a `Cache` y no a `Documents`: es un archivo de
    // paso, y el sistema puede tirarlo cuando necesite espacio sin dejarle al
    // residente una carpeta llena de códigos vencidos.
    const base64 = imagenQr.split(',')[1] ?? '';
    const { uri } = await Filesystem.writeFile({
      path: archivo,
      data: base64,
      directory: Directory.Cache,
    });

    try {
      await Share.share({ title: titulo, text: texto, files: [uri] });
      return 'compartido';
    } catch (e) {
      if (loCancelaron(e)) return 'cancelado';
      throw e;
    }
  }

  const imagen = new File([bytesDeDataUri(imagenQr)], archivo, { type: 'image/png' });

  // `canShare` con el archivo y no solo `share`: hay navegadores que tienen la
  // API pero rechazan los archivos, y ahí conviene descargar antes que fallar.
  if (navigator.canShare?.({ files: [imagen] })) {
    try {
      await navigator.share({ title: titulo, text: texto, files: [imagen] });
      return 'compartido';
    } catch (e) {
      if (loCancelaron(e)) return 'cancelado';
      // Cualquier otro fallo cae a la descarga de abajo en vez de dejar al
      // residente sin nada.
    }
  }

  const url = URL.createObjectURL(imagen);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = archivo;
  enlace.click();
  // Sin esto el blob se queda en memoria hasta que se cierre la pestaña.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'descargado';
}
